'use client';

import classNames from 'classnames';
import { usePathname, useSearchParams } from 'next/navigation';
import { FunctionComponent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PropsWithClassname } from '@/app/types';

import CzechFlagIcon from '@/app/components/CzechFlagIcon/CzechFlagIcon';
import EnglishFlagIcon from '@/app/components/EnglishFlagIcon/EnglishFlagIcon';
import { SlovakiaFlagIcon } from '@/app/components/SlovakiaFlagIcon/SlovakiaFlagIcon';
import { Language } from '@/app/enums';
import { fallbackLng } from '@/app/utils/i18n';

const getLabel = ({ currentLanguage, nextLanguage }: { currentLanguage: Language; nextLanguage: Language }) => {
  if (currentLanguage !== Language.EN) {
    return 'Switch to English';
  }

  return nextLanguage === Language.CZ ? 'Přepnout do češtiny' : 'Prepnúť do slovenčiny';
};

const iconsMap: Record<Language, ReactNode> = {
  [Language.CZ]: <CzechFlagIcon className="rounded-full" />,
  [Language.EN]: <EnglishFlagIcon className="rounded-full" />,
  [Language.SK]: <SlovakiaFlagIcon />,
};

const LanguageSwitch: FunctionComponent<PropsWithClassname> = ({ className }) => {
  const { i18n } = useTranslation();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const currentLanguage = i18n.language as Language;
  const nextLanguage = currentLanguage === fallbackLng ? Language.EN : fallbackLng;

  const handleLanguageButtonClick = async () => {
    i18n.changeLanguage(nextLanguage);

    const params = new URLSearchParams(searchParams.toString());

    if (nextLanguage === Language.EN) {
      params.set('lang', nextLanguage); // Nastaví nebo aktualizuje ?lang=XX v URL
    } else {
      params.delete('lang'); // Odstraní ?lang=XX z URL
    }

    // Router push dosnt work in this case
    window.history.replaceState(null, '', `${pathname}?${params.toString()}`);
  };

  return (
    <button
      className={classNames(className, 'flex items-center hover:text-primary-mid text-[14px] gap-[8px]')}
      onClick={handleLanguageButtonClick}
    >
      {iconsMap[nextLanguage]}
      <span>{getLabel({ currentLanguage, nextLanguage })}</span>
    </button>
  );
};

export default LanguageSwitch;
